import { useCallback, useEffect, useMemo, useState } from "react";
import {
  getExchangeCollectiveData,
  getExchangeCollectiveForecastData,
} from "../../../Rest_api/RestApi";
import { ExchangeColors, ExchangeData } from "../Exchange3/FormatData";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import { BrushStart, ChartType } from "../../../models/chart_model";
import { renderHourTick } from "../../../components/recharts/components";
import Loading from "../../../components/Loading";
import { stringToConsistentColor } from "../../../extensions/string";
import { FilterTab } from "../../../components/Filters";
import { DataTabs } from "../../Hppc/Demand/Data";
import { DynamicDatePicker } from "../../../components/DatePicker";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";

const chartSuffixes = [
  {
    suffix: "wt_mcp_rs_kwh",
    displayName: "",
    stackSuffix: "wt_mcp_rs_kwh",
  },
];

export const R_CHART_TYPES = [
  { value: ChartType.Bar, label: "Standard Bar", stackId: undefined },
  { value: ChartType.Bar, label: "Stacked Bar", stackId: "a" },
  { value: ChartType.Line, label: "Line" },
];

let Intervals = [
  {
    label: "Time Slot",
    key: "time",
    values: ["slot", "date"],
    isTime: true,
  },
  {
    label: "Date",
    key: "date",
    values: ["date"],
    isTime: false,
  },
  {
    label: "Month",
    key: "month",
    values: ["month"],
    isTime: false,
  },
];

export const H_CHART_TYPES = [
  { value: ChartType.Bar, label: "Standard Bar", stackId: undefined },
  { value: ChartType.Bar, label: "Stacked Bar", stackId: "a" },
  { value: ChartType.Line, label: "Line" },
];

export function getLastWeekDateRange() {
  // Get today's date
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Set to beginning of day

  // Get date 7 days ago
  const sevenDaysAgo = new Date(today);
  sevenDaysAgo.setDate(today.getDate() - 7);

  return {
    startDate: sevenDaysAgo,
    endDate: today,
    key: "selection",
  };
}

function formatDateToLocal(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-based
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

const PriceForcastingRTM = () => {
  const [data, setData] = useState<any[]>([]);
  const [selectedSuffix, setSelectedSuffix] = useState([chartSuffixes[0]]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(H_CHART_TYPES[2]);
  const [chartKeys, setChartKeys] = useState<string[]>([]);
  const [selectedInterval, setSelectedInterval] = useState(Intervals[0]);
  const [selectedDate, setSelectedDate] = useState<{ value: string }[]>([]);
  const [selectedslot, setSelectedslot] = useState<{ value: string }[]>([]);
  const [uniqueDate, setUniqueDate] = useState([]);
  const [uniqueslot, setUniqueslot] = useState([]);
  const [dataTab, setDataTab] = useState(DataTabs[0]);
  const [loading, setLoading] = useState(false);
  const [dates, setDate] = useState(getLastWeekDateRange());
  const [tableData, setTableData] = useState<any[]>([]);

  useEffect(() => {
    _handlefetchData();
  }, [dates]);

  async function _handlefetchData() {
    setLoading(true);

    const startDateStr = formatDateToLocal(dates.startDate);
    const endDateStr = formatDateToLocal(dates.endDate);

    const newData = await getExchangeCollectiveData(startDateStr, endDateStr);
    const forecastData = await getExchangeCollectiveForecastData(
      startDateStr,
      endDateStr,
      "RTM",
      "IEX"
    );

    setData([...forecastData, ...newData]);
    setUniqueDate(
      Array.from(
        new Set(
          newData
            .map((item: ExchangeData) => item.date)
            .sort(
              (a: string, b: string) =>
                new Date(a).getTime() - new Date(b).getTime()
            )
        )
      )
    );
    setUniqueslot(
      Array.from(new Set(newData.map((item: ExchangeData) => item.slot)))
    );
    setLoading(false);
  }

  useEffect(() => {
    let filteredData = data;

    if (selectedDate.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedDate.map((item) => item.value).includes(item.date)
      );
    }
    if (selectedslot.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedslot.map((item) => item.value).includes(item.slot)
      );
    }
    filteredData = filteredData.filter(
      (item) => item.exchange__name === "IEX" && item.product__name === "RTM"
    );

    const par: ChartDataParams = {
      keys: selectedslot.length > 0 ? ["date"] : selectedInterval.values,
      tableKeys: ["date", "slot"],
      groupBy: ["type"],
      volumeKey: ``,
      aggregationKeys: ["wt_mcp_rs_kwh", "mcv_mw"],
      priceKey: "",
      data: filteredData,

      reverseSort: ["slot", "date"],
    };
    const cData = FormatChartData(par);
    console.log(cData);
    // Sort the data by date, from latest to oldest
    const sortedData = [...cData.data].sort((a, b) => {
      // Convert dates for comparison
      const dateA = new Date(a.date || a.month || a.year || 0);
      const dateB = new Date(b.date || b.month || b.year || 0);

      // Sort descending (latest to oldest)
      return dateA.getTime() - dateB.getTime();
    });

    setChartData(sortedData);
    setChartKeys(cData.keys);
    setTableData(cData.tableData || []);
  }, [data, selectedDate, selectedInterval.values, selectedslot]);

  const handlexDataKey = useCallback(() => {
    if (selectedInterval.key === "time") {
      if (selectedslot.length) {
        return "date";
      }
      return "slot";
    } else {
      return selectedInterval.key;
    }
  }, [selectedslot.length, selectedInterval.key]);

  const xDataKey = useMemo(() => handlexDataKey(), [handlexDataKey]);

  const createChartObject = (
    key: string,
    index: number,
    suffix: string,
    displayName: string,
    stackSuffix: string,
    selectedInterval: { isTime: boolean },
    selectedChartType: { value: string; stackId?: string },
    ExchangeColors: string[]
  ) => {
    return {
      dataKey: `${key}_${suffix}`,
      name: selectedInterval.isTime
        ? `${key.replace("_", " ").replace("undefined", "")} ${displayName}`
        : displayName,
      legendColor: undefined,
      unit: suffix.includes("mcp") ? "Rs/kWh" : "KW",
      type: selectedChartType.value,
      stackId:
        selectedChartType.stackId !== undefined ? stackSuffix : undefined,
    };
  };

  function getLegends(includeActual: boolean | undefined = false) {
    return selectedSuffix.flatMap(({ suffix, displayName, stackSuffix }) =>
      chartKeys.map((key, index) =>
        createChartObject(
          key,
          index,
          suffix,
          displayName,
          stackSuffix,
          selectedInterval,
          selectedChartType as any,
          ExchangeColors
        )
      ).filter((item) => !(includeActual && item.name.includes("Actual")))
    );
  }


  const tablePar = {
    legends: getLegends(true),
    data: chartData,
    tableData: tableData,
    itemsPerPage: 96,
    unit: "Rs/KWh",
    extraKeys: selectedInterval.isTime
      ? [xDataKey, selectedInterval.values[1]]
      : [selectedInterval.values[0]],
    extraLabels: selectedInterval.values,
    filename: "Price Forecasting RTM",
    showTotal: false,
  };

  return (
    <div className="gas-page-container">
      {loading && <Loading />}

      <div className="gas-chart-container">
        <div className="gas-heading gap-x-10">
          <h2 className="chartHeading w-60">Price Forecast (Rs/KWh)</h2>
          <div className="flex gap-x-6  justify-end">
            <FilterTab
              options={DataTabs.map((item) => item.label)}
              selected={[dataTab.label]}
              onClick={(option, index) => {
                setDataTab(DataTabs[index]);
              }}
            />
            <DynamicDatePicker
              setDate={(date) => setDate(date[0]!)!}
              defaultDates={dates}
              minDate="2025-03-13"
            />

            {/* <button
              onClick={() => {
                dataTab.label === "Chart" &&
                  downloadChartTableCSV(tablePar as any);

                dataTab.label === "Summary" &&
                  downloadSummaryCSV(tablePar as any);

                dataTab.label === "Table" &&
                  downloadChartTableCSV(tablePar as any);
              }}
              className="downloadButton"
            >
              Download Data
            </button> */}
          </div>
        </div>
        <div className="h-full w-full">
          {dataTab.label === "Chart" && (
            <ReMixChart
              chartContainerHeight="calc(100vh - 250px)"
              data={chartData}
              showBrush={chartData.length > 96 ? true : undefined}
              brushStart={BrushStart.End}
              isTimeSlot={selectedslot.length < 1 && selectedInterval.isTime}
              onlyTitle={!(selectedslot.length < 1 && selectedInterval.isTime)}
              barCategoryGap={10}
              unit={"Rs/KWh"}
              yAxisLabel={"Rs/KWh"}
              legends={getLegends() as any}
              xDataKey={xDataKey}
              nameGap={40}
              xTick={
                selectedInterval.isTime && selectedslot.length == 0
                  ? (value: any, index: any) => {
                      return renderHourTick(value, 10);
                    }
                  : undefined
              }
              secondXDataKey={
                selectedInterval.isTime && selectedslot.length == 0
                  ? selectedInterval.values[1]
                  : undefined
              }
            />
          )}
          {dataTab.label === "Summary" && (
            <LegendSummaryTable {...(tablePar as any)} />
          )}
          {dataTab.label === "Table" && <ChartTable {...(tablePar as any)} />}
        </div>
      </div>
    </div>
  );
};

export default PriceForcastingRTM;
